import { Component, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import LiveBadge from "@/components/LiveBadge";
import Curtain from "@/components/Curtain";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Timeline from "@/components/Timeline";
import Schedule from "@/components/Schedule";
import Houses from "@/components/Houses";
import CampusMap from "@/components/CampusMap";
import Quiz from "@/components/Quiz";
import Guestbook from "@/components/Guestbook";
import WishGallery from "@/components/WishGallery";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import WishAdmin from "@/components/WishAdmin";
import ScrollProgress from "@/components/ScrollProgress";
import CursorGlow from "@/components/CursorGlow";
import DoscoBot from "@/components/DoscoBot";
import "@/App.css";

class ErrorBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink font-display text-sm tracking-widest text-gold">
          SOMETHING BROKE — REFRESH TO RETURN TO CHANDBAGH
        </div>
      );
    }
    return this.props.children;
  }
}

function FounderDayPage() {
  const [theme, setTheme] = useState(() => localStorage.getItem("doon-theme") || "dark");
  const [venue, setVenue] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("doon-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (window.location.hash.includes("reset-token=")) {
      const token = window.location.hash.split("reset-token=")[1]?.split("&")[0] || "";
      if (token) {
        setResetToken(token);
        setAuthOpen(true);
      }
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf;
    let lenis;
    (async () => {
      const Lenis = (await import("lenis")).default;
      lenis = new Lenis({
        lerp: 0.095,
        smoothWheel: true,
        wheelMultiplier: 0.95,
        touchMultiplier: 1.4,
      });
      window.__lenis = lenis;
      const loop = (t) => {
        lenis.raf(t);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    })();
    return () => {
      cancelAnimationFrame(raf);
      lenis?.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <AuthProvider>
      <div className="grain min-h-screen bg-ink font-body text-fg antialiased" data-testid="app-root">
      <Curtain />
      <Navbar theme={theme} onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} onOpenAuth={() => setAuthOpen(true)} />
      <main>
        <Hero />
        <Marquee />
        <Timeline />
        <Schedule venue={venue} onSelectVenue={setVenue} onClearVenue={() => setVenue(null)} />
        <Houses />
        <CampusMap
          activeVenue={venue}
          onExplore={(v) => {
            setVenue(v);
            if (v) document.querySelector("#schedule")?.scrollIntoView({ behavior: "smooth" });
          }}
        />
        <Quiz />
        <Guestbook />
        <WishGallery />
      </main>
      <Footer onOpenAdmin={() => setAdminOpen(true)} />
      <LiveBadge />
      <DoscoBot />
      <ScrollProgress />
      <CursorGlow />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} resetToken={resetToken} onResetDone={() => setResetToken(null)} />
      <WishAdmin open={adminOpen} onClose={() => setAdminOpen(false)} onOpenAuth={() => setAuthOpen(true)} />
      <Toaster position="bottom-right" theme={theme} />
      </div>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <FounderDayPage />
    </ErrorBoundary>
  );
}
